import { defineStore } from "pinia";
import type { IEditor } from "board-core";
import { BatchScheduler } from "board-core";
import { fabric } from "fabric";
import { ref, computed, shallowRef } from "vue";
import { SpatialIndexManager } from "../optimization";

/**
 * 编辑器实例状态管理 Store
 * 
 * 职责：
 * 1. 管理 Editor 引擎实例的生命周期（初始化/销毁）
 * 2. 提供对底层 fabric.Canvas 对象的访问
 * 3. 管理与画布渲染同步相关的运行时状态
 * 
 * 注意：
 * - 此 Store 中的状态（如 editor 实例）通常是不可序列化的，不应持久化到本地存储
 * - 仅用于运行时组件访问底层能力
 */
export const useEditorStore = defineStore("infinite-canvas-editor", () => {
  // #region State - 运行时实例
  /** Editor 核心实例，包含画布操作的所有底层 API */
  const editor = shallowRef<IEditor | null>(null);

  /**
   * 分批处理调度中心，与画布生命周期绑定。
   * 所有需要分批遍历 canvas 对象的模块共享同一个调度器，
   * 保证：统一帧时间预算、全局版本号取消、画布切换时一键清理。
   */
  let batchScheduler: BatchScheduler | null = null;

  /**
   * 空间索引管理器，用于优化大量对象的查询性能。
   * 通过网格分区实现 O(k) 的视口查询（k 为视口内对象数）。
   */
  let spatialIndex: SpatialIndexManager | null = null;

  /** 空间索引 object:modified 监听器引用，用于动态销毁时解绑 */
  let spatialIndexModifiedHandler: ((e: any) => void) | null = null;

  /** 阈值检测的 add/remove 监听器，用于 setEditor 切换时解绑 */
  let thresholdCheckHandlers: { added: (e: any) => void; removed: (e: any) => void } | null = null;

  /** 创建阈值：>= 500 时创建；销毁阈值：< 400 时销毁，避免在边界反复创建/销毁 */
  const SPATIAL_INDEX_CREATE_THRESHOLD = 500;
  const SPATIAL_INDEX_DESTROY_THRESHOLD = 400;

  function getObjectCount(canvas: fabric.Canvas): number {
    return canvas.getObjects().filter((o: any) => o.id !== "workspace").length;
  }

  function createSpatialIndexForCanvas(canvas: fabric.Canvas): void {
    if (spatialIndex) return;

    const objectCount = getObjectCount(canvas);
    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();
    const estimatedObjectCount = Math.max(objectCount, 50);
    const targetDensity = 8;
    const gridSize = Math.max(
      400,
      Math.min(
        2048,
        Math.sqrt((canvasWidth * canvasHeight) / (estimatedObjectCount / targetDensity))
      )
    );

    spatialIndex = new SpatialIndexManager(Math.round(gridSize));

    const onModified = (e: any) => {
      if (e.target && e.target.id !== "workspace") {
        spatialIndex?.updateObject(e.target);
      }
    };
    spatialIndexModifiedHandler = onModified;
    canvas.on("object:modified", onModified);

    const existingObjects = canvas.getObjects().filter((obj: any) => obj.id !== "workspace");
    spatialIndex.addObjects(existingObjects);
  }

  function destroySpatialIndexForCanvas(canvas: fabric.Canvas): void {
    if (!spatialIndex || !spatialIndexModifiedHandler) return;

    canvas.off("object:modified", spatialIndexModifiedHandler);
    spatialIndexModifiedHandler = null;

    spatialIndex.clear();
    spatialIndex = null;
  }

  /** 
   * 画布状态同步锁 
   * 用于在执行 undo/redo 或加载快照时，暂停某些自动保存或响应逻辑，防止循环触发
   */
  const isSyncingCanvasState = ref(false);
  /**
   * 当前鼠标在画布中的位置（场景坐标）
   * 使用普通变量存储，避免 60fps 更新触发 Vue 响应式导致 46% 性能瓶颈
   * 仅 pasteLayers 粘贴时读取，无需响应式
   */
  let _mousePosition: { x: number; y: number } | null = null;

  /** 是否正在拖入文件（全局状态） */
  const isDraggingFiles = ref(false);
  /** 是否正在拖入画布（局部状态） */
  const isDraggingOverCanvas = ref(false);
  // #endregion

  // #region Getters - 实例访问
  /** 
   * 获取底层的 fabric.Canvas 对象
   * 用于直接操作 FabricJS API（如手动渲染、事件绑定等）
   */
  const fabricCanvas = computed<fabric.Canvas | null | undefined>(() => {
    return editor.value?.fabricCanvas;
  });
  // #endregion

  // #region Actions - 实例管理
  /**
   * 设置编辑器实例（通常在 Canvas 组件挂载时调用）
   * @param newEditor 编辑器实例
   */
  function setEditor(newEditor: IEditor | null) {
    // 切换/销毁画布时，先清理旧调度器的所有待处理任务
    if (batchScheduler) {
      batchScheduler.destroy();
      batchScheduler = null;
    }

    // 清理空间索引与阈值检测监听器（从旧 canvas 解绑）
    const oldCanvas = editor.value?.fabricCanvas;
    if (oldCanvas) {
      if (spatialIndexModifiedHandler) {
        oldCanvas.off("object:modified", spatialIndexModifiedHandler);
      }
      if (thresholdCheckHandlers) {
        oldCanvas.off("object:added", thresholdCheckHandlers.added);
        oldCanvas.off("object:removed", thresholdCheckHandlers.removed);
      }
    }
    spatialIndexModifiedHandler = null;
    thresholdCheckHandlers = null;
    if (spatialIndex) {
      spatialIndex.clear();
      spatialIndex = null;
    }

    editor.value = newEditor;
    _mousePosition = null; // 切换画布时清空，避免使用过期坐标

    if (newEditor) {
      // 🔥 性能优化：针对大量对象场景（1000+），适当增加帧预算
      batchScheduler = new BatchScheduler(12);
      (newEditor as any).batchScheduler = batchScheduler;

      const canvas = newEditor.fabricCanvas;
      if (canvas) {
        // 初始：根据当前对象数决定是否创建空间索引
        const initialCount = getObjectCount(canvas);
        if (initialCount >= SPATIAL_INDEX_CREATE_THRESHOLD) {
          createSpatialIndexForCanvas(canvas);
        }

        // 始终监听增删，动态创建/销毁空间索引（400↔500 滞后避免抖动）
        const onAdded = (e: any) => {
          if (e.target?.id === "workspace") return;
          if (spatialIndex) {
            spatialIndex.addObject(e.target);
          } else if (getObjectCount(canvas) >= SPATIAL_INDEX_CREATE_THRESHOLD) {
            createSpatialIndexForCanvas(canvas);
          }
        };
        const onRemoved = (e: any) => {
          if (spatialIndex) {
            spatialIndex.removeObject(e.target);
            if (getObjectCount(canvas) < SPATIAL_INDEX_DESTROY_THRESHOLD) {
              destroySpatialIndexForCanvas(canvas);
            }
          }
        };
        thresholdCheckHandlers = { added: onAdded, removed: onRemoved };
        canvas.on("object:added", onAdded);
        canvas.on("object:removed", onRemoved);
      }
    }
  }

  /**
   * 获取分批处理调度中心。
   * 画布存活期间始终返回同一个实例，画布销毁后返回 null。
   */
  function getBatchScheduler(): BatchScheduler | null {
    return batchScheduler;
  }

  /**
   * 获取空间索引管理器。
   * 用于快速查询视口内的对象，优化大量对象场景的性能。
   */
  function getSpatialIndex(): SpatialIndexManager | null {
    return spatialIndex;
  }


  /**
   * 设置同步锁状态
   * @param isSyncing 是否正在同步状态
   */
  function setSyncingState(isSyncing: boolean) {
    isSyncingCanvasState.value = isSyncing;
  }

  /**
   * 更新鼠标位置（非响应式，避免触发 Vue 更新链）
   * @param pos 坐标对象
   */
  function updateMousePosition(pos: { x: number; y: number } | null) {
    _mousePosition = pos;
  }

  /**
   * 获取当前鼠标位置（粘贴时使用）
   */
  function getMousePosition(): { x: number; y: number } | null {
    return _mousePosition;
  }

  /**
   * 设置拖拽状态
   * @param dragging 是否正在拖拽
   */
  function setDraggingFiles(dragging: boolean) {
    isDraggingFiles.value = dragging;
  }

  /**
   * 设置画布拖拽状态
   * @param dragging 是否正在画布上方拖拽
   */
  function setDraggingOverCanvas(dragging: boolean) {
    isDraggingOverCanvas.value = dragging;
  }
  // #endregion

  return {
    editor,
    isSyncingCanvasState,
    fabricCanvas,
    getMousePosition,
    isDraggingFiles,
    isDraggingOverCanvas,
    setEditor,
    getBatchScheduler,
    getSpatialIndex,
    setSyncingState,
    updateMousePosition,
    setDraggingFiles,
    setDraggingOverCanvas
  };
});
